import { type Configuration, PublicClientApplication } from "@azure/msal-browser"

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payloadBase64 = token.split(".")[1]
    return JSON.parse(atob(payloadBase64))
  } catch {
    return null
  }
}

export interface TokenValidationResult {
  isValid: boolean
  errors: string[]
  payload: Record<string, unknown> | null
}

export function validateAccessToken(accessToken: string): TokenValidationResult {
  const payload = decodeJwtPayload(accessToken)
  const errors: string[] = []

  if (!payload) {
    return { isValid: false, errors: ["Kunne ikke decode token"], payload: null }
  }

  const aud = payload.aud as string | undefined
  const scp = payload.scp as string | undefined
  const exp = payload.exp as number | undefined

  // Check aud starts with "api://"
  if (!aud?.startsWith("api://")) {
    errors.push(`aud skal starte med "api://", fik: ${aud}`)
  }

  // Check scp contains "access_as_user"
  if (!scp?.includes("access_as_user")) {
    errors.push(`scp skal indeholde "access_as_user", fik: ${scp}`)
  }

  // Check exp is in the future
  if (exp) {
    const now = Math.floor(Date.now() / 1000)
    if (exp <= now) {
      errors.push(`Token er udløbet (exp: ${new Date(exp * 1000).toISOString()})`)
    }
  } else {
    errors.push("Token mangler exp claim")
  }

  return {
    isValid: errors.length === 0,
    errors,
    payload,
  }
}

export function logTokenDetails(accessToken: string) {
  if (process.env.NODE_ENV !== "development") return

  const payload = decodeJwtPayload(accessToken)
  if (!payload) {
    console.error("[v0] Failed to decode token payload")
    return
  }

  console.log("[v0] Token details:", {
    iss: payload.iss,
    ver: payload.ver,
    aud: payload.aud,
    scp: payload.scp,
    exp: payload.exp,
    expDate: payload.exp ? new Date((payload.exp as number) * 1000).toISOString() : "N/A",
  })
}

export const msalConfig: Configuration = {
  auth: {
    clientId: process.env.NEXT_PUBLIC_AZURE_CLIENT_ID || "",
    authority:
      process.env.NEXT_PUBLIC_AZURE_AUTHORITY ??
      `https://login.microsoftonline.com/${process.env.NEXT_PUBLIC_AZURE_TENANT_ID || "common"}/v2.0`,
    redirectUri: typeof window !== "undefined" ? window.location.origin : "",
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
}

// Initialize MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig)

export const loginRequest = {
  scopes: process.env.NEXT_PUBLIC_API_SCOPE ? [process.env.NEXT_PUBLIC_API_SCOPE] : [],
}
